/**
 * Financial Planning — forecast calculation engine.
 *
 * Computes daily P&L, Balance Sheet, and Cashflow given driver inputs
 * and opening balances. Aggregates to ISO weeks (Monday-start) or calendar months.
 *
 * MODEL ASSUMPTIONS:
 * - Drivers (sales, purchases) are entered as monthly totals ex-GST.
 *   Each monthly total is split evenly across the days in that calendar month.
 * - Sales: revenue + AR (incl-GST) + GST output recognised on sale day.
 *   Cash collected 30 days after sale (1-month terms).
 * - Purchases: deposit paid on order day → "Deposits to Suppliers" asset.
 *   On shipping day: deposit transfers to inventory (ex-GST), AP recognised
 *   for the balance (incl-GST), GST input claimed.
 *   Balance is paid `balanceDaysAfterShipping` after the shipping date.
 *   Type A: ship 3d after order,  balance 30d after shipping
 *   Type B: ship 30d after order, balance 60d after shipping
 *   Type C: ship 30d after order, balance 90d after shipping
 * - COGS = revenue × cogsPct (ex-GST), recognised on sale day; inventory drops accordingly.
 * - OpEx flat per month, paid daily incl-GST.
 * - Existing AR/AP from Xero invoices: cash flows on actual due date.
 *   Past-due → collected/paid on day 0.
 * - Other Assets / Other Liabilities from Xero kept constant throughout forecast.
 */

import dayjs from 'dayjs';

const GST_RATE = 0.10;
const GST_MULT = 1 + GST_RATE;

// shippingDays: order day → goods shipped (and AP / inventory recognised)
// balanceDaysAfterShipping: shipping day → balance payment due
export const PURCHASE_TERMS = {
  A: { depositPct: 0.10, shippingDays: 3,  balanceDaysAfterShipping: 30 },
  B: { depositPct: 0.20, shippingDays: 30, balanceDaysAfterShipping: 60 },
  C: { depositPct: 0.20, shippingDays: 30, balanceDaysAfterShipping: 90 },
};

const SALES_TERMS_DAYS = 30; // 1-month terms

function round(x) { return Math.round((x || 0) * 100) / 100; }

// Compute Monday of the ISO week containing a given date (returns dayjs)
function isoMonday(d) {
  const day = d.day(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? 6 : day - 1;
  return d.subtract(diff, 'day').startOf('day');
}

/**
 * Pad explicit drivers up to horizon (3 explicit months, then flat extrapolation).
 */
export function expandMonthlyDrivers(explicitMonths, horizonMonths) {
  const last = explicitMonths[explicitMonths.length - 1] || { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 };
  const out = [];
  for (let i = 0; i < horizonMonths; i++) {
    out.push(explicitMonths[i] || { ...last });
  }
  return out;
}

/**
 * Main forecast.
 *
 * inputs:
 *   - openingBalances: { cash, accountsReceivable, inventory, accountsPayable,
 *                        depositsToSuppliers, gstPayable, otherAssets, otherLiabilities }
 *   - monthlyDrivers: per-month { sales, purchaseA, purchaseB, purchaseC } (length = horizonMonths, ex-GST)
 *   - cogsPct: decimal (e.g. 0.75)
 *   - monthlyOpex: flat opex per month (ex-GST)
 *   - horizonMonths: number of months to forecast
 *   - existingAR / existingAP: array of { amountDue, dueDate (ISO) }
 *   - startDate: ISO date string for day 0 (engine uses this as baseline)
 *
 * returns: { days: [{ date (ISO), pl, bs, cf }, ...] }
 */
export function computeForecast({
  openingBalances,
  monthlyDrivers,
  cogsPct,
  monthlyOpex,
  horizonMonths,
  existingAR = [],
  existingAP = [],
  startDate,
}) {
  const start = dayjs(startDate).startOf('day');
  const end = start.add(horizonMonths, 'month');
  const totalDays = end.diff(start, 'day');

  // Daily flow arrays
  const cashIn = new Array(totalDays).fill(0);
  const cashOut = new Array(totalDays).fill(0);
  const arChange = new Array(totalDays).fill(0);
  const apChange = new Array(totalDays).fill(0);
  const inventoryChange = new Array(totalDays).fill(0);
  const depositsChange = new Array(totalDays).fill(0);
  const gstChange = new Array(totalDays).fill(0);
  const revenueArr = new Array(totalDays).fill(0);
  const cogsArr = new Array(totalDays).fill(0);
  const opexArr = new Array(totalDays).fill(0);
  // Track new cash flows separately for CF detail rows
  const cashOutPurchasesArr = new Array(totalDays).fill(0);
  const cashOutOpexArr = new Array(totalDays).fill(0);

  function schedulePurchase(dayIdx, amount, type) {
    if (amount === 0) return;
    const { depositPct, shippingDays, balanceDaysAfterShipping } = PURCHASE_TERMS[type];
    const grossAmount = amount * GST_MULT;
    const deposit = grossAmount * depositPct;
    const balance = grossAmount * (1 - depositPct);

    // Day of order: deposit paid (cash out, deposits-to-suppliers up)
    cashOut[dayIdx] += deposit;
    cashOutPurchasesArr[dayIdx] += deposit;
    depositsChange[dayIdx] += deposit;

    // Shipping day: inventory in (ex-GST), deposit transfers out, AP for balance
    const shippingDay = dayIdx + shippingDays;
    if (shippingDay < totalDays) {
      depositsChange[shippingDay] -= deposit;
      inventoryChange[shippingDay] += amount; // ex-GST
      apChange[shippingDay] += balance;
      gstChange[shippingDay] -= amount * GST_RATE; // input GST claimable (reduces GST payable)
    }

    // Balance payment day = shipping day + N
    const balanceDay = dayIdx + shippingDays + balanceDaysAfterShipping;
    if (balanceDay < totalDays) {
      cashOut[balanceDay] += balance;
      cashOutPurchasesArr[balanceDay] += balance;
      apChange[balanceDay] -= balance;
    }
    // (If shippingDay or balanceDay extend past horizon, those flows simply don't fire.)
  }

  // Distribute monthly drivers across days
  for (let m = 0; m < horizonMonths; m++) {
    const monthStart = start.add(m, 'month');
    const monthEnd = monthStart.add(1, 'month');
    const daysInMonth = monthEnd.diff(monthStart, 'day');
    const driver = monthlyDrivers[m] || { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 };

    const dailySales = (driver.sales || 0) / daysInMonth;
    const dailyA = (driver.purchaseA || 0) / daysInMonth;
    const dailyB = (driver.purchaseB || 0) / daysInMonth;
    const dailyC = (driver.purchaseC || 0) / daysInMonth;
    const dailyOpex = (monthlyOpex || 0) / daysInMonth;

    for (let d = 0; d < daysInMonth; d++) {
      const dayIdx = monthStart.add(d, 'day').diff(start, 'day');
      if (dayIdx < 0 || dayIdx >= totalDays) continue;

      // Sales recognised today (revenue ex-GST, AR incl-GST, GST output)
      revenueArr[dayIdx] += dailySales;
      arChange[dayIdx] += dailySales * GST_MULT;
      gstChange[dayIdx] += dailySales * GST_RATE;
      const cashSaleDay = dayIdx + SALES_TERMS_DAYS;
      if (cashSaleDay < totalDays) {
        cashIn[cashSaleDay] += dailySales * GST_MULT;
        arChange[cashSaleDay] -= dailySales * GST_MULT;
      }

      // OpEx (ex-GST in P&L, paid incl-GST today)
      opexArr[dayIdx] += dailyOpex;
      cashOut[dayIdx] += dailyOpex * GST_MULT;
      cashOutOpexArr[dayIdx] += dailyOpex * GST_MULT;
      gstChange[dayIdx] -= dailyOpex * GST_RATE;

      // Purchases (deposit today, delivery & balance scheduled later)
      schedulePurchase(dayIdx, dailyA, 'A');
      schedulePurchase(dayIdx, dailyB, 'B');
      schedulePurchase(dayIdx, dailyC, 'C');

      // COGS recognised on sale day; matches revenue
      cogsArr[dayIdx] += dailySales * cogsPct;
      inventoryChange[dayIdx] -= dailySales * cogsPct;
    }
  }

  // Schedule existing Xero invoices on their due dates (past-due → day 0)
  for (const inv of existingAR) {
    if (!inv.dueDate || !inv.amountDue) continue;
    const offset = Math.max(0, dayjs(inv.dueDate).startOf('day').diff(start, 'day'));
    if (offset >= totalDays) continue;
    cashIn[offset] += inv.amountDue;
    arChange[offset] -= inv.amountDue;
  }
  for (const inv of existingAP) {
    if (!inv.dueDate || !inv.amountDue) continue;
    const offset = Math.max(0, dayjs(inv.dueDate).startOf('day').diff(start, 'day'));
    if (offset >= totalDays) continue;
    cashOut[offset] += inv.amountDue;
    cashOutPurchasesArr[offset] += inv.amountDue;
    apChange[offset] -= inv.amountDue;
  }

  // Walk day-by-day, accumulating BS state
  const days = [];
  let cash = openingBalances.cash || 0;
  let ar = openingBalances.accountsReceivable || 0;
  let inventory = openingBalances.inventory || 0;
  let ap = openingBalances.accountsPayable || 0;
  let deposits = openingBalances.depositsToSuppliers || 0;
  let gstPayable = openingBalances.gstPayable || 0;
  const otherAssets = openingBalances.otherAssets || 0;
  const otherLiabilities = openingBalances.otherLiabilities || 0;
  let retainedEarnings = 0;

  for (let d = 0; d < totalDays; d++) {
    cash += cashIn[d] - cashOut[d];
    ar += arChange[d];
    ap += apChange[d];
    inventory += inventoryChange[d];
    deposits += depositsChange[d];
    gstPayable += gstChange[d];
    const np = revenueArr[d] - cogsArr[d] - opexArr[d];
    retainedEarnings += np;

    const totalAssets = cash + ar + inventory + deposits + otherAssets;
    const totalLiabilities = ap + gstPayable + otherLiabilities;

    days.push({
      date: start.add(d, 'day').format('YYYY-MM-DD'),
      pl: {
        revenue: round(revenueArr[d]),
        cogs: round(cogsArr[d]),
        gp: round(revenueArr[d] - cogsArr[d]),
        opex: round(opexArr[d]),
        np: round(np),
      },
      bs: {
        cash: round(cash),
        ar: round(ar),
        inventory: round(inventory),
        deposits: round(deposits),
        otherAssets: round(otherAssets),
        totalAssets: round(totalAssets),
        ap: round(ap),
        gstPayable: round(gstPayable),
        otherLiabilities: round(otherLiabilities),
        totalLiabilities: round(totalLiabilities),
        netAssets: round(totalAssets - totalLiabilities),
        retainedEarnings: round(retainedEarnings),
      },
      cf: {
        cashIn: round(cashIn[d]),
        cashOutPurchases: round(cashOutPurchasesArr[d]),
        cashOutOpex: round(cashOutOpexArr[d]),
        netCf: round(cashIn[d] - cashOut[d]),
        closingCash: round(cash),
      },
    });
  }

  return { days };
}

// ============================================================================
// ShineHub Residential — project-based simulator
// ============================================================================

/**
 * Synthesize future projects from sales drivers and run the install queue.
 *
 * Lifecycle: closing → install (single event). Install date is a proxy
 * sourced from MongoDB customerWelcomePackAt for real projects, or
 * assigned by the install driver (FIFO by closingDate) for the rest.
 *
 * inputs:
 *   - existingPipeline: real projects { id, name, paymentType, revenue, crmPayment, closingDate, installDate? }
 *   - monthlyDrivers: per-month { totalSales, salesUpfront, salesPPA, salesBNPL, totalInstalls }
 *   - assumptions: {
 *       byType: { Upfront: {avgRevenue, avgCrmPayment, cogsPct}, PPA: {...}, BNPL: {...} },
 *       avgClosingToInstallDays, monthlyOpex,
 *     }
 *   - openingBalances, horizonMonths, startDate
 *
 * Returns: { days, pipeline }
 *
 * Per-project schedule:
 *   Upfront:
 *     closing+7d : revenue accrued (P&L) + AR up (rev × 1.10) + GST output;
 *                  cash in 10% × pay × 1.10 (AR -=)
 *     install−14d: cash in 80% × pay × 1.10 (AR -=)
 *     install+14d: cash in (rev − 90% × pay) × 1.10 (AR -=)
 *   PPA/BNPL:
 *     install+0d : revenue accrued + AR up (rev × 1.10) + GST output
 *     install+14d: cash in rev × 1.10 (AR -=)
 *   All types COGS:
 *     install+0d : COGS expensed + AP up (cogs × 1.10) + GST input claimed
 *     install+7d : cash out cogs × 1.10 (AP -=)
 */
export function computeShForecast({
  existingPipeline = [],
  monthlyDrivers,
  assumptions,
  openingBalances,
  horizonMonths,
  startDate,
  today = null,                // ISO date string; if set + monthActuals provided, current-month synth subtracts MTD actuals
  monthActuals = [],            // [{ closings, installs }] aligned with monthlyDrivers (only used if includeExistingActuals)
  includeExistingActuals = false,
}) {
  const start = dayjs(startDate).startOf('day');
  const end = start.add(horizonMonths, 'month');
  const totalDays = end.diff(start, 'day');

  const dayOfStart = (date) => dayjs(date).startOf('day').diff(start, 'day');

  // Extrapolate drivers to horizonMonths (last entry repeats forward)
  const lastDriver = monthlyDrivers[monthlyDrivers.length - 1] || {};
  const expandedDrivers = Array.from({ length: horizonMonths }, (_, i) =>
    monthlyDrivers[i] || { ...lastDriver }
  );

  // 1. Synthesize future closings per-payment-type, each spread across working days
  const todayDayjs = today ? dayjs(today).startOf('day') : null;
  const synthesized = [];
  for (let m = 0; m < horizonMonths; m++) {
    const monthStart = start.add(m, 'month');
    const monthEnd = monthStart.add(1, 'month');
    const isCurrentMonth = todayDayjs && includeExistingActuals
      && todayDayjs.isAfter(monthStart.subtract(1, 'day')) && todayDayjs.isBefore(monthEnd);
    const wdStart = isCurrentMonth ? todayDayjs.clone() : monthStart;
    const workingDays = [];
    for (let d = wdStart.clone(); d.isBefore(monthEnd); d = d.add(1, 'day')) {
      const dow = d.day();
      if (dow >= 1 && dow <= 5) workingDays.push(d.format('YYYY-MM-DD'));
    }
    if (workingDays.length === 0) continue;
    const monthLabel = monthStart.format('MMM');
    const rawDriver = expandedDrivers[m] || {};
    const actualClosings = isCurrentMonth ? (monthActuals[m]?.closings || 0) : 0;
    const synthBudget = Math.max(0, (rawDriver.totalSales || 0) - actualClosings);
    const totalSales = rawDriver.totalSales || 0;
    const ratio = totalSales > 0 ? synthBudget / totalSales : 0;
    const driver = {
      salesUpfront: Math.round((rawDriver.salesUpfront || 0) * ratio),
      salesPPA: Math.round((rawDriver.salesPPA || 0) * ratio),
      salesBNPL: Math.max(0, synthBudget
        - Math.round((rawDriver.salesUpfront || 0) * ratio)
        - Math.round((rawDriver.salesPPA || 0) * ratio)),
    };

    const distributeType = (count, paymentType) => {
      if (count <= 0) return;
      const typeAssump = assumptions.byType?.[paymentType] || {};
      // Deterministic placement: item i lands at workingDays[floor((i + 0.5) * N / count)].
      // (Floating-point cumulative-sum approach was buggy — e.g. 1/22 × 22 < 1 left no items.)
      for (let i = 0; i < count; i++) {
        const targetIdx = Math.min(
          workingDays.length - 1,
          Math.floor((i + 0.5) * workingDays.length / count)
        );
        const dStr = workingDays[targetIdx];
        synthesized.push({
          id: `synth-${m}-${paymentType}-${i + 1}`,
          name: `${monthLabel}'s ${paymentType} Sales-${i + 1}`,
          paymentType,
          revenue: typeAssump.avgRevenue || 0,
          crmPayment: paymentType === 'PPA' ? 0 : (typeAssump.avgCrmPayment || 0),
          cogsPct: typeAssump.cogsPct ?? 0.75,
          closingDate: dStr,
          closingEstimated: true,
          installDate: null,
          installEstimated: true,
          synthetic: true,
        });
      }
    };

    distributeType(driver.salesUpfront || 0, 'Upfront');
    distributeType(driver.salesPPA || 0, 'PPA');
    distributeType(driver.salesBNPL || 0, 'BNPL');
  }

  // 2. Combine real + synth, sort by closingDate ASC (FIFO)
  const allProjects = [
    ...existingPipeline.map(p => ({
      ...p,
      synthetic: false,
      closingEstimated: false,
      installEstimated: !p.installDate, // missing → will be set by install loop
    })),
    ...synthesized,
  ].sort((a, b) => (a.closingDate || '').localeCompare(b.closingDate || ''));

  // 3. Install loop driven by totalInstalls. FIFO by closingDate ASC.
  // Constraint: install date must be ≥ closingDate + closingToInstallDays (min lag).
  const closingToInstallDays = assumptions.avgClosingToInstallDays || 90;
  const installQueue = allProjects
    .filter(p => !p.installDate)
    .sort((a, b) => (a.closingDate || '').localeCompare(b.closingDate || ''));
  let installHead = 0;

  /**
   * What the allocator actually did each month, and why it stopped.
   *
   * Without this the page has to infer the reason from the numbers, and the
   * obvious inference is wrong: the backlog it can see is not the backlog the
   * queue can draw from. A project the CRM has already given an install date
   * is excluded from the queue above, and of the rest only those that closed
   * at least the lag before the month's last working day are reachable. So a
   * month can fall short with hundreds of projects apparently waiting, and the
   * only honest explanation names which of the two ran out.
   */
  const installPlan = [];

  for (let m = 0; m < horizonMonths; m++) {
    const monthStart = start.add(m, 'month');
    const monthEnd = monthStart.add(1, 'month');
    const isCurrentMonth = todayDayjs && includeExistingActuals
      && todayDayjs.isAfter(monthStart.subtract(1, 'day')) && todayDayjs.isBefore(monthEnd);
    const driverInstalls = expandedDrivers[m]?.totalInstalls || 0;
    const actualInstalls = isCurrentMonth ? (monthActuals[m]?.installs || 0) : 0;
    const installsThisMonth = Math.max(0, driverInstalls - actualInstalls);
    // The latest closing date that can still install inside this month.
    const cutoff = monthEnd.subtract(1, 'day').subtract(closingToInstallDays, 'day').format('YYYY-MM-DD');
    const eligible = installQueue
      .filter(p => !p.installDate && p.closingDate && p.closingDate <= cutoff).length;
    const waiting = installQueue.filter(p => !p.installDate).length;
    const record = {
      month: monthStart.format('YYYY-MM-01'),
      target: installsThisMonth,
      placed: 0,
      // Still unplaced and reachable this month, versus still unplaced at all.
      eligible,
      waiting,
      cutoff,
      reason: null,
    };
    installPlan.push(record);
    if (installsThisMonth <= 0) continue;
    const wdStart = isCurrentMonth ? todayDayjs.clone() : monthStart;
    const workingDays = [];
    for (let d = wdStart.clone(); d.isBefore(monthEnd); d = d.add(1, 'day')) {
      const dow = d.day();
      if (dow >= 1 && dow <= 5) workingDays.push(d.format('YYYY-MM-DD'));
    }
    if (workingDays.length === 0) continue;
    // Deterministic cumulative target: by end of working day i, want floor((i+1) × N / D) installs done.
    // (Same FP fix as synth distribution.)
    let installedSoFar = 0;
    for (let i = 0; i < workingDays.length; i++) {
      const dStr = workingDays[i];
      const wantBy = Math.floor((i + 1) * installsThisMonth / workingDays.length);
      while (installedSoFar < wantBy) {
        while (installHead < installQueue.length && installQueue[installHead].installDate) installHead++;
        if (installHead >= installQueue.length) { record.reason = 'queue-empty'; break; }
        const next = installQueue[installHead];
        if (!next.closingDate) { record.reason = 'queue-empty'; break; }
        const earliest = dayjs(next.closingDate).add(closingToInstallDays, 'day').format('YYYY-MM-DD');
        // The head is not eligible yet, and the queue is in closing order, so
        // nothing behind it is either. Later working days in this month may
        // still reach it, which is why this only breaks out of the day.
        if (earliest > dStr) { record.reason = 'lag'; break; }
        next.installDate = dStr;
        next.installEstimated = true;
        installHead++;
        installedSoFar++;
      }
      if (installedSoFar >= installsThisMonth) break;
    }
    record.placed = installedSoFar;
    if (installedSoFar >= installsThisMonth) record.reason = null;
  }

  // 4. Daily flow arrays
  const cashIn = new Array(totalDays).fill(0);
  const cashOut = new Array(totalDays).fill(0);
  const arChange = new Array(totalDays).fill(0);
  const apChange = new Array(totalDays).fill(0);
  const inventoryChange = new Array(totalDays).fill(0);
  const gstChange = new Array(totalDays).fill(0);
  const revenueArr = new Array(totalDays).fill(0);
  const cogsArr = new Array(totalDays).fill(0);
  const opexArr = new Array(totalDays).fill(0);
  const cashOutPurchasesArr = new Array(totalDays).fill(0);
  const cashOutOpexArr = new Array(totalDays).fill(0);

  // Schedule helper. Past events (dayIdx < 0) are NOT re-booked —
  // they're assumed to be already realized and reflected in the opening BS.
  function scheduleAt(dayIdx, key, amount) {
    if (dayIdx < 0 || dayIdx >= totalDays) return;
    if (key === 'cashIn') cashIn[dayIdx] += amount;
    else if (key === 'cashOutPurchases') { cashOut[dayIdx] += amount; cashOutPurchasesArr[dayIdx] += amount; }
    else if (key === 'arChange') arChange[dayIdx] += amount;
    else if (key === 'apChange') apChange[dayIdx] += amount;
    else if (key === 'gstChange') gstChange[dayIdx] += amount;
    else if (key === 'revenue') revenueArr[dayIdx] += amount;
    else if (key === 'cogs') cogsArr[dayIdx] += amount;
  }

  for (const p of allProjects) {
    const closingIdx = p.closingDate ? dayOfStart(p.closingDate) : null;
    const installIdx = p.installDate ? dayOfStart(p.installDate) : null;

    const rev = p.revenue || 0;          // ex-GST
    const pay = p.crmPayment || 0;        // ex-GST
    const projCogsPct = p.cogsPct != null ? p.cogsPct : (assumptions.byType?.[p.paymentType]?.cogsPct ?? 0.75);
    const cogs = rev * projCogsPct;

    if (p.paymentType === 'Upfront') {
      // closing+7d: revenue accrual + AR up + GST output + 10% deposit cash in (AR -=)
      if (closingIdx != null) {
        const recogIdx = closingIdx + 7;
        const tier1 = pay * 0.10 * GST_MULT;
        scheduleAt(recogIdx, 'revenue', rev);
        scheduleAt(recogIdx, 'gstChange', rev * GST_RATE);
        scheduleAt(recogIdx, 'arChange', rev * GST_MULT);
        scheduleAt(recogIdx, 'cashIn', tier1);
        scheduleAt(recogIdx, 'arChange', -tier1);
      }
      if (installIdx != null) {
        // install-14d: 80% deposit
        const tier2Idx = installIdx - 14;
        const tier2 = pay * 0.80 * GST_MULT;
        scheduleAt(tier2Idx, 'cashIn', tier2);
        scheduleAt(tier2Idx, 'arChange', -tier2);
        // install+14d: balance
        const tier3Idx = installIdx + 14;
        const tier3 = (rev - 0.90 * pay) * GST_MULT;
        scheduleAt(tier3Idx, 'cashIn', tier3);
        scheduleAt(tier3Idx, 'arChange', -tier3);
      }
    } else {
      // PPA / BNPL: install+0d revenue accrual (P&L + AR up + GST output);
      //            install+14d full cash payment (AR -=)
      if (installIdx != null) {
        const total = rev * GST_MULT;
        scheduleAt(installIdx, 'revenue', rev);
        scheduleAt(installIdx, 'gstChange', rev * GST_RATE);
        scheduleAt(installIdx, 'arChange', total);
        const cashIdx = installIdx + 14;
        scheduleAt(cashIdx, 'cashIn', total);
        scheduleAt(cashIdx, 'arChange', -total);
      }
    }

    // COGS for ALL types: install+0d accrue (P&L + AP up + GST input), install+7d cash payment (AP down)
    if (installIdx != null) {
      const cogsCashIdx = installIdx + 7;
      const cogsIncl = cogs * GST_MULT;
      scheduleAt(installIdx, 'cogs', cogs);
      scheduleAt(installIdx, 'apChange', cogsIncl);
      scheduleAt(installIdx, 'gstChange', -cogs * GST_RATE);
      scheduleAt(cogsCashIdx, 'cashOutPurchases', cogsIncl);
      scheduleAt(cogsCashIdx, 'apChange', -cogsIncl);
    }
  }

  // 6. OpEx (flat, daily, ex-GST, paid incl-GST). Same OpEx every month of forecast horizon.
  for (let m = 0; m < horizonMonths; m++) {
    const monthStart = start.add(m, 'month');
    const monthEnd = monthStart.add(1, 'month');
    const daysInMonth = monthEnd.diff(monthStart, 'day');
    const dailyOpex = (assumptions.monthlyOpex || 0) / daysInMonth;
    for (let d = 0; d < daysInMonth; d++) {
      const dayIdx = monthStart.add(d, 'day').diff(start, 'day');
      if (dayIdx < 0 || dayIdx >= totalDays) continue;
      opexArr[dayIdx] += dailyOpex;
      cashOut[dayIdx] += dailyOpex * GST_MULT;
      cashOutOpexArr[dayIdx] += dailyOpex * GST_MULT;
      gstChange[dayIdx] -= dailyOpex * GST_RATE;
    }
  }

  // 7. Walk day-by-day
  const days = [];
  let cash = openingBalances.cash || 0;
  let ar = openingBalances.accountsReceivable || 0;
  let inventory = openingBalances.inventory || 0;
  let ap = openingBalances.accountsPayable || 0;
  let gstPayable = openingBalances.gstPayable || 0;
  const otherAssets = openingBalances.otherAssets || 0;
  const otherLiabilities = openingBalances.otherLiabilities || 0;
  let retained = 0;

  for (let d = 0; d < totalDays; d++) {
    cash += cashIn[d] - cashOut[d];
    ar += arChange[d];
    ap += apChange[d];
    inventory += inventoryChange[d];
    gstPayable += gstChange[d];
    const np = revenueArr[d] - cogsArr[d] - opexArr[d];
    retained += np;
    const totalAssets = cash + ar + inventory + otherAssets;
    const totalLiabilities = ap + gstPayable + otherLiabilities;
    days.push({
      date: start.add(d, 'day').format('YYYY-MM-DD'),
      pl: {
        revenue: round(revenueArr[d]),
        cogs: round(cogsArr[d]),
        gp: round(revenueArr[d] - cogsArr[d]),
        opex: round(opexArr[d]),
        np: round(np),
      },
      bs: {
        cash: round(cash),
        ar: round(ar),
        inventory: round(inventory),
        deposits: 0,
        otherAssets: round(otherAssets),
        totalAssets: round(totalAssets),
        ap: round(ap),
        gstPayable: round(gstPayable),
        otherLiabilities: round(otherLiabilities),
        totalLiabilities: round(totalLiabilities),
        netAssets: round(totalAssets - totalLiabilities),
        retainedEarnings: round(retained),
      },
      cf: {
        cashIn: round(cashIn[d]),
        cashOutPurchases: round(cashOutPurchasesArr[d]),
        cashOutOpex: round(cashOutOpexArr[d]),
        netCf: round(cashIn[d] - cashOut[d]),
        closingCash: round(cash),
      },
    });
  }

  return { days, pipeline: allProjects, installPlan };
}

/**
 * Aggregate daily forecast rows by ISO week (Monday-Sunday) or calendar month.
 * Flow items (P&L, CF) summed; stock items (BS balances) take period-end snapshot.
 * Label format: 'D MMM' for weekly, 'MMM YYYY' for monthly.
 */
export function aggregateByPeriod(days, period /* 'week' | 'month' */) {
  const groups = new Map();
  for (const d of days) {
    const date = dayjs(d.date);
    const key = period === 'week'
      ? isoMonday(date).format('YYYY-MM-DD')
      : date.format('YYYY-MM');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(d);
  }
  const sortedKeys = [...groups.keys()].sort();
  return sortedKeys.map(key => {
    const group = groups.get(key);
    const last = group[group.length - 1];
    const sumPl = (k) => group.reduce((s, d) => s + d.pl[k], 0);
    const sumCf = (k) => group.reduce((s, d) => s + d.cf[k], 0);
    return {
      key,
      label: period === 'week'
        ? dayjs(key).format('D MMM')
        : dayjs(key + '-01').format('MMM YYYY'),
      pl: {
        revenue: round(sumPl('revenue')),
        cogs: round(sumPl('cogs')),
        gp: round(sumPl('gp')),
        opex: round(sumPl('opex')),
        np: round(sumPl('np')),
      },
      bs: { ...last.bs }, // end-of-period snapshot
      cf: {
        cashIn: round(sumCf('cashIn')),
        cashOutPurchases: round(sumCf('cashOutPurchases')),
        cashOutOpex: round(sumCf('cashOutOpex')),
        netCf: round(sumCf('netCf')),
        closingCash: last.bs.cash,
      },
    };
  });
}
